/**
 * CommitmentViewPage — P3 of the Decision Propagation & Lineage PRD.
 *
 * Route: /planning/plans/:cycleId/commitments/:itemId
 * (gated: `commitment_view` module, resolved against the plan cycle's workspace).
 *
 * The moment this page exists for is Patton's: someone two levels from a
 * decision is asked why they are doing this, and today they guess. The pieces
 * of the real answer are all recorded — immutable decisions with a four-part
 * rationale, the items the commitment beat, declared resources, attached
 * stopping rules, a version history with change reasons, an authorizer — but
 * they live on seven different surfaces. This page is the first place they are
 * one object.
 *
 * READ-ONLY. There is no action on this page, by design (PRD §5: "no new write
 * paths; this is assembly"). A canonical view that could also edit would become
 * a second place a commitment can be changed, which is the drift it exists to
 * expose.
 *
 * THE RULE THAT SHAPES THE LAYOUT: a section with nothing in it states why it
 * is empty. A blank panel reads as "the system is broken"; "no resources were
 * declared against this commitment" reads as what it is — a fact about the
 * organization, and usually the most interesting thing on the page.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FileText, RefreshCw } from 'lucide-react';
import { Panel, Callout, EmptyState, Skeleton, DescriptionList } from '../../components/ds';
import { CommitmentStaleMarker } from '../../components/planning/CommitmentStaleMarker';
import { fetchCommitmentView } from '../../lib/commitmentViewService';
import {
  SECTION_QUESTIONS,
  pattonTest,
  type CommitmentView,
  type SectionKey,
} from '../../lib/commitmentViewCore';
import { COVERAGE_LABELS } from '../../lib/rejectedAlternatives';
import { narrateBasis, parseBasis } from '../../lib/porProvenanceBasis';
import { useCommitmentViewEnabled } from '../../hooks/useCommitmentViewEnabled';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import { useUserStore } from '../../stores/prioritization/userStore';
import { isAdminRole } from '../../types/prioritization/roles';
import { ModuleOffNotice } from '../../components/shared/ModuleOffNotice';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * One question, answered or explained.
 *
 * The question is the heading — not a noun label. "Instead of what?" is the
 * thing a reader is actually asking; "Alternatives" is a database column.
 */
function Section({
  sectionKey,
  view,
  children,
}: {
  sectionKey: SectionKey;
  view: CommitmentView;
  children: React.ReactNode;
}) {
  const state = view.sections[sectionKey];
  return (
    <Panel title={SECTION_QUESTIONS[sectionKey]}>
      {state.answered ? (
        children
      ) : (
        <p className="text-sm text-[var(--text-ter)]">{state.absence}</p>
      )}
    </Panel>
  );
}

export function CommitmentViewPage() {
  const { cycleId, itemId } = useParams<{ cycleId: string; itemId: string }>();
  const [view, setView] = useState<CommitmentView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!cycleId || !itemId) return;
    setLoading(true);
    setError(null);
    const result = await fetchCommitmentView({ planCycleId: cycleId, itemId });
    setView(result.view);
    setError(result.error ?? null);
    setLoading(false);
  }, [cycleId, itemId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !view) {
    return (
      <div className="p-6">
        <Callout tone="coral" title="This commitment could not be assembled">
          {error ?? 'No commitment was found for this plan cycle and item.'}
        </Callout>
      </div>
    );
  }

  if (!view.exists) {
    return (
      <div className="p-6">
        <EmptyState
          icon={FileText}
          title="No commitment here"
          description={
            view.item
              ? `“${view.item.title ?? 'This item'}” is not carried by any plan version in this cycle, so there is nothing committed to read.`
              : 'The item behind this commitment could not be found.'
          }
        />
      </div>
    );
  }

  const patton = pattonTest(view);
  const basis = parseBasis(view.authority.sourceCycleReportBasis);

  return (
    <div className="space-y-4 p-6">
      {/* Header: identity, version, and how complete the record actually is. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-[var(--text-pri)]">
              {view.item?.title ?? 'Untitled commitment'}
            </h1>
            <CommitmentStaleMarker
              quotedVersionNumber={view.currentState?.versionNumber ?? null}
              authoritativeVersionNumber={view.authority.authorizedVersionNumber}
              reading={view.staleness}
            />
          </div>
          <p className="mt-1 text-sm text-[var(--text-ter)]">
            {view.cycleName ? `In ${view.cycleName}` : 'Plan commitment'} ·{' '}
            <Link
              to={`/planning/plans/${view.identity.planCycleId}`}
              className="underline hover:text-[var(--text-sec)]"
            >
              plan overview
            </Link>
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/*
        The Patton readout leads, and it leads with the failure case. A view
        that cannot answer "why" is the whole problem stated on one line — and
        it must not be reachable only by scrolling past six sections that
        happen to be full.
      */}
      <Callout tone={patton.passes ? 'slate' : 'mustard'} title={patton.summary}>
        {view.staleness.detail}
      </Callout>

      <Section sectionKey="what" view={view}>
        <DescriptionList
          items={[
            { label: 'Status', value: view.currentState?.status ?? '—' },
            {
              label: 'Position',
              value:
                view.currentState?.priorityPosition === null ||
                view.currentState?.priorityPosition === undefined
                  ? '—'
                  : `#${view.currentState.priorityPosition}`,
            },
            { label: 'Certainty', value: view.currentState?.certaintyLevel ?? '—' },
            {
              label: 'Latest version',
              value: view.currentState ? `v${view.currentState.versionNumber}` : '—',
            },
            {
              label: 'Authorized version',
              value:
                view.authority.authorizedVersionNumber === null
                  ? 'None — this cycle has no authorized Plan of Record'
                  : `v${view.authority.authorizedVersionNumber}`,
            },
          ]}
        />
        {view.item?.description && (
          <p className="mt-3 text-sm text-[var(--text-sec)]">{view.item.description}</p>
        )}
      </Section>

      <Section sectionKey="why" view={view}>
        <div className="space-y-4">
          {view.decisions.map((decision) => (
            <div key={decision.decisionId} className="border-l-2 border-[var(--rule)] pl-3">
              <div className="text-sm font-medium text-[var(--text-pri)]">{decision.title}</div>
              <div className="text-xs text-[var(--text-ter)]">
                {formatDate(decision.decidedAt)}
                {decision.decidedByName ? ` · ${decision.decidedByName}` : ''}
              </div>
              {decision.rationaleContext && (
                <p className="mt-2 text-sm text-[var(--text-sec)]">
                  <span className="text-[var(--text-ter)]">Context: </span>
                  {decision.rationaleContext}
                </p>
              )}
              {decision.rationaleReasoning && (
                <p className="mt-1 text-sm text-[var(--text-sec)]">
                  <span className="text-[var(--text-ter)]">Reasoning: </span>
                  {decision.rationaleReasoning}
                </p>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section sectionKey="instead_of_what" view={view}>
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wide text-[var(--text-ter)]">
            {COVERAGE_LABELS[view.alternativesCoverage]}
          </div>
          {view.alternativesNarration && (
            <p className="text-sm text-[var(--text-sec)]">{view.alternativesNarration}</p>
          )}
          {view.alternatives.length > 0 && (
            <ul className="space-y-1 text-sm text-[var(--text-sec)]">
              {view.alternatives.map((alt) => (
                <li key={alt.itemId} className="flex items-baseline gap-2">
                  <span className="text-[var(--text-ter)]">
                    #{alt.rankAtDecision ?? '?'}
                  </span>
                  <span>{alt.title ?? 'Untitled item'}</span>
                  {alt.cutReason && (
                    <span className="text-[var(--text-ter)]">— {alt.cutReason}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {view.decisions
            .map((d) => d.rationaleAlternatives)
            .filter((p): p is string => Boolean(p?.trim()))
            .slice(0, 1)
            .map((prose) => (
              <p key="prose" className="text-sm text-[var(--text-sec)]">
                <span className="text-[var(--text-ter)]">Also considered: </span>
                {prose}
              </p>
            ))}
        </div>
      </Section>

      <Section sectionKey="at_what_cost" view={view}>
        <ul className="space-y-1 text-sm text-[var(--text-sec)]">
          {view.claims.map((claim) => (
            <li key={claim.id}>
              {claim.resourceType ?? claim.claimKind}
              {claim.declaredHours !== null ? ` — ${claim.declaredHours}h` : ''}
              {claim.declaredAmount !== null
                ? ` — ${claim.declaredAmount} ${claim.currency ?? ''}`.trimEnd()
                : ''}
              <span className="ml-2 text-[var(--text-ter)]">{claim.status}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section sectionKey="under_what_conditions" view={view}>
        <ul className="space-y-2 text-sm text-[var(--text-sec)]">
          {view.terms.map((term) => (
            <li key={term.id}>
              <span className="text-[var(--text-ter)]">{term.kind}: </span>
              {term.conditionText}
              <span className="ml-2 text-[var(--text-ter)]">
                {term.status}
                {term.reviewBy ? ` · review by ${term.reviewBy}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <Section sectionKey="how_it_has_changed" view={view}>
        <ol className="space-y-2 text-sm text-[var(--text-sec)]">
          {view.changeHistory.map((entry) => (
            <li key={entry.planVersionId} className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium text-[var(--text-pri)]">v{entry.versionNumber}</span>
              <span>{entry.status}</span>
              {entry.priorityPosition !== null && (
                <span className="text-[var(--text-ter)]">#{entry.priorityPosition}</span>
              )}
              {entry.changeReason && (
                <span className="text-[var(--text-ter)]">· {entry.changeReason}</span>
              )}
              {entry.dropReason && (
                <span className="text-[var(--text-ter)]">· {entry.dropReason}</span>
              )}
              <span className="text-[var(--text-ter)]">{formatDate(entry.createdAt)}</span>
            </li>
          ))}
        </ol>
      </Section>

      <Section sectionKey="who" view={view}>
        <DescriptionList
          items={[
            { label: 'Authorized by', value: view.authority.authorizedBy ?? '—' },
            { label: 'Authorized at', value: formatDate(view.authority.authorizedAt) },
            {
              label: 'Decided by',
              value:
                view.decisions.map((d) => d.decidedByName).filter(Boolean).join(', ') || '—',
            },
            {
              /*
               * P1's basis, rendered rather than hidden. Every environment
               * today shows a blank cycle report because migration 698 is not
               * applied anywhere — so this row is the difference between the
               * reader concluding "the trail is broken" and reading the actual
               * reason the link is absent.
               */
              label: 'Traced to cycle report',
              value: view.authority.sourceCycleReportId ?? narrateBasis(basis),
            },
          ]}
        />
      </Section>
    </div>
  );
}

export default CommitmentViewPage;

/**
 * §2.7 gate for the route, colocated with the page it gates.
 *
 * Two things this has to get right, and both have bitten this repo before.
 * The gate resolves against the PLAN CYCLE's workspace, not the session's — a
 * plan reached by URL can belong to another workspace, and resolving against
 * the session silently falls back to "off" (the 2026-08-21 defect
 * `useModuleGateForWorkspace` exists for). And it waits for the workspace to
 * resolve before deciding, because gating on a not-yet-loaded module reads as
 * off and would bounce a permitted user off their own route on every cold load.
 *
 * It lives here rather than in PlanningApp so the router file takes a one-line
 * diff (it is a shared surface across concurrent sessions) and so the gate is
 * mountable in a test — the failure class that matters is a crash on mount,
 * which tsc and eslint have both passed before.
 */
export function CommitmentViewRoute() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const currentRole = useUserStore((s) => s.currentRole);
  const [workspaceId, setWorkspaceId] = useState<string | null | undefined>(undefined);
  const enabled = useCommitmentViewEnabled(workspaceId);

  useEffect(() => {
    let cancelled = false;
    if (!cycleId) {
      setWorkspaceId(null);
      return;
    }
    void getTypedSupabaseClient()
      .from('plan_cycles')
      .select('workspace_id')
      .eq('id', cycleId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setWorkspaceId(data?.workspace_id ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [cycleId]);

  if (workspaceId === undefined) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[var(--text-ter)]">
        Loading…
      </div>
    );
  }
  if (!enabled) {
    // An admin who followed a commitment link deserves to know the module is
    // off rather than be bounced back to the plan with no explanation — the
    // same silent-gate defect as the decision-latency route (2026-09-02).
    if (currentRole && isAdminRole(currentRole)) {
      return (
        <ModuleOffNotice
          moduleName="Canonical commitment view"
          whatItDoes="It assembles one read-only page per commitment: what was committed, why, instead of what, at what cost, under what conditions, how it has changed, and who authorized it."
        />
      );
    }
    return <Navigate to={`/planning/plans/${cycleId ?? ''}`} replace />;
  }
  return <CommitmentViewPage />;
}
